import React, { useEffect, useState } from "react";
import { getPurchaseOrder, downloadProjectPO, deletePurchaseOrder } from "../../actions/project-management.action";
import { getGlobalSettingTemplateTypes } from "../../actions/global-setting-template.action";
import idx from "idx";
import { numberFormat } from "../../util/currency-formatter";
import Table from "react-bootstrap/Table";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Button, Popover, OverlayTrigger } from "react-bootstrap";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useSearchParams, useNavigate } from "react-router-dom";
import { VENDOR_TAB, VENDOR_URL_PARAM, VENDOR_TYPE } from "../../constants/vendor.contant";
import { PROJECT_MANAGEMENT, VENDOR_PAYMENT, VENDOR, VENDOR_INVOICES } from "../../constants/route-paths";
import DownloadDialog from "../download-dialog";
import DeleteDialog from "../delete-dialog";
import PayInvoice from "../../routes/vendor-payment/pay-invoice";

/**
 * Component for viewing Purchase Order details, line items, attachments, and actions.
 *
 * @param {Object} props - Component properties.
 * @param {string|number} props.poId - The Purchase Order ID.
 * @param {Function} [props.cancelHandler] - Handler for canceling/closing the view.
 * @param {Function} [props.editHandler] - Handler for editing the Purchase Order.
 * @param {string} [props.sourcePage] - Source navigation identifier.
 * @param {boolean} [props.showHeading=true] - Whether to render page header.
 * @param {React.ReactNode} [props.breadcrumb=null] - Optional breadcrumb component.
 * @returns {React.ReactElement} View Purchase Order component.
 */
function ViewPurchaseOrderComponent({
  poId,
  cancelHandler,
  editHandler,
  sourcePage,
  showHeading = true,
  breadcrumb = null,
}) {
  const [PO, setPO] = useState({});
  const [vendorType, setvendorType] = useState();
  const [total, setTotal] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [downloadData, setDownloadData] = useState({ open: false, id: "" });
  const [templateOptions, setTemplateOptions] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showPayInvoice, setShowInvoice] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const entitlements = useSelector((state) => state.user?.entitlements || {});
  const userAccessControl = useSelector((state) => state.user?.userAccessControl);
  const writePermission = userAccessControl === 2 || userAccessControl === undefined;

  useEffect(() => {
    if (poId) getPO();
  }, [poId]);

  const getPO = async () => {
    const query = `?poId=${poId}`;
    let res = await dispatch(getPurchaseOrder(query));
    setPO(res);
    setvendorType(idx(res, (_) => _.vendor.type));
    let totalCost = 0;
    Array.isArray(res.items) &&
      res.items.forEach(({ total }) => (totalCost += total));
    setTotal(totalCost);
    const attachmentList = Array.isArray(idx(res, (_) => _.attachment))
      ? idx(res, (_) => _.attachment)
      : [];
    const attachment = attachmentList.map((item, index) => {
      return (
        <div key={item.id || item.name || index} className="attachedFiles">
          <p style={{ margin: "0 10px 0 0" }}>{item.name}</p>
        </div>
      );
    });
    setAttachments(attachment);
  };

  // Determine if edit should be enabled based on source page and status
  const isEditEnabled = () => {
    const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);
    const poStatus = idx(PO, _ => _.status);
    const materialStatus = idx(PO, _ => _.materialstatus);

    // Backend may return '1'/'2'/'3' (DB codes) or 'None Received'/'Partially Received'/'Fully Received'
    const isNoneReceived = materialStatus === 'None Received' || materialStatus === '1' || materialStatus === 1;

    // If coming from Orders List (vendor orders page) - check PO Status
    if (returnTab === VENDOR_TAB.PURCHASE_ORDERS || sourcePage === 'orders-list') {
      return poStatus === 'Pending';
    }

    // If coming from Project PO List (project execution) - check Material Status
    if (sourcePage === 'project-po-list' || (!returnTab && !sourcePage)) {
      return isNoneReceived;
    }

    // Default fallback - use existing logic
    return poStatus === 'Pending';
  };

  // Get appropriate edit button message based on source
  const getEditButtonMessage = () => {
    const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);

    // If coming from Orders List (vendor orders page)
    if (
      returnTab === VENDOR_TAB.PURCHASE_ORDERS ||
      sourcePage === "orders-list"
    ) {
      return "Only Pending POs can be edited.";
    }

    // If coming from Project PO List (project execution)
    if (sourcePage === "project-po-list" || (!returnTab && !sourcePage)) {
      return "Only POs with None Received Stocks can be edited.";
    }

    // Default message
    return "Only Pending POs can be edited.";
  };

  const gettemplate = async (type) => {
    try {
      let newdata = [];
      const templateData = await dispatch(getGlobalSettingTemplateTypes(type, null));
      if (Array.isArray(templateData)) {
        templateData.forEach((item) => {
          newdata.push({
            value: item.id,
            key: item.name,
          });
        });
      }
      setTemplateOptions(newdata);
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    }
  };

  const handleDownloadDialog = (id = "", templateType = "purchase_order") => {
    const actualId = id || poId;
    setDownloadData((prev) => {
      const isOpening = !prev.open;
      const newDownloadData = { ...prev, open: isOpening, id: actualId || "" };
      if (isOpening) {
        gettemplate(templateType);
      }
      return newDownloadData;
    });
  };

  const downloadFile = async (fileFormat = "pdf", tempId) => {
    try {
      await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId));
      handleDownloadDialog();
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  const deleteDialogState = () => {
    setOpenDeleteDialog((prev) => !prev);
  };

  const deleteHandler = async () => {
    deleteDialogState();
    if (poId) {
      await dispatch(deletePurchaseOrder(poId, "Purchase Order Deleted successfully"));
      if (cancelHandler) cancelHandler();
    }
  };

  /**
   * Toggles the Receive Invoice modal visibility and refreshes PO details only upon successful invoice submission.
   *
   * @param {boolean} modalOpen - Whether to show or hide the Receive Invoice modal.
   * @param {boolean} [isSuccess=false] - Whether the invoice was successfully submitted.
   */
  const handlePayInvoiceModal = (modalOpen, isSuccess = false) => {
    setShowInvoice(modalOpen);
    if (!modalOpen && isSuccess) {
      getPO();
    }
  };

  const receiveStock = () => {
    const projId = PO.projectId || idx(PO, (_) => _.project.id);
    const vendId = PO.vendorId || idx(PO, (_) => _.vendor.id);
    const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);
    const returnSubTab = searchParams.get(VENDOR_URL_PARAM.RETURN_SUB_TAB);
    let query = "";
    if (returnTab) query += `?returnTab=${returnTab}`;
    if (returnSubTab) query += `${query ? "&" : "?"}returnSubTab=${returnSubTab}`;
    navigate(`/${PROJECT_MANAGEMENT}/receive-stock/${projId}/${vendId}/${poId}${query}`);
  };

  const returnStock = () => {
    const projId = PO.projectId || idx(PO, (_) => _.project.id);
    const vendId = PO.vendorId || idx(PO, (_) => _.vendor.id);
    navigate(`/${PROJECT_MANAGEMENT}/return-stock/${projId}/${vendId}/${poId}`);
  };

  const viewInvoices = () => {
    const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);
    if (returnTab === VENDOR_TAB.PURCHASE_ORDERS || sourcePage === "orders-list") {
      navigate(`/${VENDOR}/${VENDOR_INVOICES}?tab=supplier-invoices&poId=${poId}&type=project`);
    } else {
      const projId = PO.projectId || idx(PO, (_) => _.project.id);
      navigate(`/${VENDOR_PAYMENT}?project=${projId}&poId=${poId}`);
    }
  };

  const closeMenuAnd = (callback) => {
    document.body.click();
    if (callback) callback();
  };

  const isFullyReceived =
    PO.materialstatus === "Fully Received" ||
    PO.materialstatus === "Over Received" ||
    PO.materialstatus === "2" ||
    PO.materialstatus === 2;

  const isFullyInvoiced =
    PO.invoicestatus === "Fully Invoiced" ||
    PO.invoicestatus === "3" ||
    PO.invoicestatus === 3;

  const hasLineItemsReceived = Array.isArray(PO.items) && PO.items.some(
    (item) => parseFloat(item.receivedQty || 0) > 0
  );
  const isStockReceived = (PO.materialstatus !== "None Received" && PO.materialstatus !== "1") || hasLineItemsReceived;

  const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);
  const isFromVendorOrders = returnTab === VENDOR_TAB.PURCHASE_ORDERS || sourcePage === "orders-list";

  const subTab = searchParams.get("returnSubTab") || searchParams.get("subTab");
  const isCompletedPO =
    !isFromVendorOrders && (
      subTab === "inactive" ||
      (sourcePage === "project-po-list"
        ? isFullyReceived && isFullyInvoiced
        : PO.status === "Completed" || PO.status === "Finished" || PO.status === "Inactive")
    );

  const renderMenu = (
    <Popover id="popover-basic" data-cy="po-view-action-menu">
      <Popover.Body>
        <ul className="action-block-list mb-0">
          {isCompletedPO ? (
            <>
              <li onClick={() => closeMenuAnd(() => handleDownloadDialog(poId))} data-cy="download-po">
                Download PO
              </li>
              {writePermission && (
                <li onClick={() => closeMenuAnd(returnStock)} data-cy="return-stock">
                  Return Stock
                </li>
              )}
              <li onClick={() => closeMenuAnd(viewInvoices)} data-cy="view-invoices">
                View Invoices
              </li>
            </>
          ) : (
            <>
              {isEditEnabled() && writePermission && (
                <li onClick={() => closeMenuAnd(editHandler)} data-cy="edit-po">
                  Edit PO
                </li>
              )}
              <li onClick={() => closeMenuAnd(() => handleDownloadDialog(poId))} data-cy="download-po">
                Download PO
              </li>
              {!PO.is_credit_invoice && writePermission && !isFullyReceived && (
                <li
                  className={!(entitlements?.receiveStock) ? "disabled" : ""}
                  onClick={() => entitlements?.receiveStock && closeMenuAnd(receiveStock)}
                  data-cy="receive-stock"
                >
                  Receive Stock
                </li>
              )}
              {!PO.is_credit_invoice && writePermission && isStockReceived && (
                <li onClick={() => closeMenuAnd(returnStock)} data-cy="return-stock">
                  Return Stock
                </li>
              )}
              {!PO.is_credit_invoice && writePermission && !isFullyInvoiced && (
                <li onClick={() => closeMenuAnd(() => handlePayInvoiceModal(true))} data-cy="receive-invoice">
                  Receive Invoice
                </li>
              )}
              <li onClick={() => closeMenuAnd(viewInvoices)} data-cy="view-invoices">
                View Invoices
              </li>
              {writePermission && (PO.status === "Pending" || sourcePage === "project-po-list") && (
                <li onClick={() => closeMenuAnd(deleteDialogState)} data-cy="delete-po">
                  Delete
                </li>
              )}
            </>
          )}
        </ul>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <DownloadDialog
        open={downloadData.open}
        handleDownloadDialog={handleDownloadDialog}
        downloadFile={downloadFile}
        templateOptions={templateOptions}
        downloadModuleName="Purchase Order"
        dataCy="download-po-dialog"
      />
      <DeleteDialog
        open={openDeleteDialog}
        id={poId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName="Purchase Order"
        description={`Purchase Order - ${PO.f_id || PO.id}`}
        dataCy="delete-po-dialog"
      />
      {showPayInvoice && (
        <PayInvoice
          open={showPayInvoice}
          handleClose={(isSuccess = false) => handlePayInvoiceModal(false, isSuccess)}
          projectId={PO.projectId || idx(PO, (_) => _.project.id)}
          vendorId={PO.vendorId || idx(PO, (_) => _.vendor.id)}
          poId={poId}
          poNo={PO.f_id || PO.id}
          projectName={idx(PO, (_) => _.project.projectName) || idx(PO, (_) => _.projectName)}
          vendorName={idx(PO, (_) => _.vendor.business_name) || idx(PO, (_) => _.vendorName)}
          initialVendorType={idx(PO, (_) => _.vendor.type) || vendorType}
          isSubContractor={idx(PO, (_) => _.vendor.type) === VENDOR_TYPE.SUB_CONTRACTOR}
          workType="project"
          payInvoiceType="purchaseOrder"
          handleDownloadDialog={handleDownloadDialog}
          dataCy="receive-invoice-po-modal"
        />
      )}

      {showHeading && (
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h2 className="content-heading text-uppercase mb-0" data-cy="heading">
            View Purchase Order
          </h2>
          <div className="action-dropdown me-4">
            <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderMenu}>
              <Button variant="default" data-cy="action-menu-button">
                <MoreHorizIcon />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      )}

      {breadcrumb}
      <div className="row" data-cy="po-details">
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="po-no">
            <label>Purchase Order Number</label>
            {/* f_id is the PO number with project prefix and Suffix if null then default to po Number*/}
            <p>{PO.f_id || PO.id}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="vendor-type">
            <label>Vendor Type</label>
            <p>{vendorType}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="vendor-name">
            <label>Vendor Name</label>
            <p>{idx(PO, (_) => _.vendor.business_name) || "-"}</p>
          </div>
        </div>
        {idx(PO, (_) => _.rfq.description) && (
          <div className="col-lg-4 mt-3">
            <div className="view-block" data-cy="description">
              <label>Description</label>
              <p>{idx(PO, (_) => _.rfq.description)}</p>
            </div>
          </div>
        )}
        {idx(PO, (_) => _.quotation.id) && (
          <div className="col-4 mt-3">
            <div className="view-block" data-cy="quote-request-no">
              <label>Quote Request No</label>
              <p>{idx(PO, (_) => _.quotation.id)}</p>
            </div>
          </div>
        )}
        {PO.quoteNo && (
          <div className="col-4 mt-3">
            <div className="view-block" data-cy="quote-no">
              <label>Quote No</label>
              <p>{PO.quoteNo}</p>
            </div>
          </div>
        )}

        <div className="col-4 mt-3">
          <div className="view-block" data-cy="delivery-date">
            <label>Delivery Date</label>
            <p>
              {idx(PO, (_) => _.deliveryDate)
                ? moment(idx(PO, (_) => _.deliveryDate)).format("DD/MM/YYYY")
                : "-"}
            </p>
          </div>
        </div>

        <div className="col-4 mt-3">
          <div className="view-block" data-cy="date-ordered">
            <label>Date Ordered</label>
            <p>
              {(() => {
                const createdAt = idx(PO, (_) => _.createdAt);
                return createdAt ? moment(createdAt).format("DD/MM/YYYY") : "-";
              })()}
            </p>
          </div>
        </div>

        <div className="col-4 mt-3">
          <div className="view-block" data-cy="delivery-option">
            <label>Delivery Option</label>
            <p>{idx(PO, (_) => _.deliveryOption) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="notes">
            <label>Notes</label>
            <p>{idx(PO, (_) => _.notes) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="street-address">
            <label>Street Address</label>
            <p>{idx(PO, (_) => _.street_address) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="suburb">
            <label>Suburb</label>
            <p>{idx(PO, (_) => _.suburb) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="state">
            <label>State</label>
            <p>{idx(PO, (_) => _.state) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="postcode">
            <label>Postcode</label>
            <p>{idx(PO, (_) => _.postcode) || "-"}</p>
          </div>
        </div>
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="country">
            <label>Country</label>
            <p>{idx(PO, (_) => _.country) || "-"}</p>
          </div>
        </div>
        {Array.isArray(idx(PO, (_) => _.contacts)) &&
          PO.contacts.length > 0 && (
            <div className="col-4 mt-3">
              <div className="view-block" data-cy="contacts">
                <label>Contacts</label>
                {PO.contacts.map((contact) => (
                  <p
                    key={`${contact.type}-${contact.id}`}
                    className="mb-0"
                  >
                    {[
                      contact.name,
                      contact.type === "USER" ? "User" : "Customer",
                    ]
                      .filter(Boolean)
                      .join(" - ") || "-"}
                  </p>
                ))}
              </div>
            </div>
          )}
        <div className="col-4 mt-3">
          <div className="view-block" data-cy="total">
            <label>Total</label>
            <p>{numberFormat(total)}</p>
          </div>
        </div>
        {idx(PO, (_) => _.is_credit_invoice) && (
          <div className="col-4 mt-3">
            <div className="view-block">
              <label>Credit Invoice</label>
              <p>
                {numberFormat(idx(PO, (_) => _.receive_credit_amount) || 0)}
              </p>
            </div>
          </div>
        )}
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="attachments">
            <label>Attachments</label>
            <p>{attachments}</p>
          </div>
        </div>
      </div>

      <div className="material-list-block mt-3" data-cy="material-list">
        <div className="table-responsive">
          <Table
            bordered
            className="table-create table-material table-material-ch"
          >
            <thead>
              <tr>
                <th className="w-auto" data-cy="task">
                  Task
                </th>
                <th className="w-auto" data-cy="item">
                  Item
                </th>
                <th className="w-auto" data-cy="manufacturer">
                  Manufacturer
                </th>
                <th data-cy="part-no">Part Number</th>
                <th data-cy="qty">Qty</th>
                <th data-cy="qty-received">Qty Received</th>
                <th className="text-right" data-cy="quotation">
                  Quotation
                </th>
              </tr>
            </thead>
            <tbody data-cy="material-list-body">
              {(PO.items || []).map((item) => (
                <tr key={item.id} data-cy="material-list-item">
                  <td data-cy="task">{item.taskName}</td>
                  <td data-cy="item">{item.item}</td>
                  <td data-cy="manufacturer">{item.manufacturer}</td>
                  <td data-cy="part-no">{item.partNo}</td>
                  <td data-cy="qty">{item.qty}</td>
                  <td data-cy="qty-received">{item.receivedQty}</td>
                  <td className="text-right" data-cy="quotation">
                    {numberFormat(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div className="col-12 d-flex mt-3 justify-content-end">
        <Button
          type="button"
          onClick={isEditEnabled() ? editHandler : () => { }}
          className={`bg-brand-1 me-3 ${!isEditEnabled() ? "disabled" : ""}`}
          data-cy="edit-button"
          title={!isEditEnabled() ? getEditButtonMessage() : ""}
        >
          EDIT
        </Button>
        <Button
          type="button"
          onClick={cancelHandler}
          className="secondarybtn cancelbtn"
          data-cy="cancel-button"
        >
          CANCEL
        </Button>
      </div>
      {!isEditEnabled() && (
        <div className="text-right mt-1" data-cy="edit-warning">
          <em className=" text-nowrap brand-color-1 fst-12 fw-semibold">
            *{getEditButtonMessage()}
          </em>
        </div>
      )}
    </>
  );
}

export default ViewPurchaseOrderComponent;
